"use client";

import React, { type ButtonHTMLAttributes } from "react";

import styles from "@/components/common/ResultsSliderBase/Results.module.scss";
import { useResultsContext } from "@/components/common/ResultsSliderBase/ResultsContext";
import Picture from "@/components/ui-kit/Picture/Picture";
import { cn } from "@/utils/general";

type ThumbnailProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  index?: number;
  thumbnail: string;
  name?: string;
};

export function ResultsThumbnail({ index = 0, thumbnail, name, className }: ThumbnailProps) {
  const { activeIndex, setActiveIndex, autoplay, setAutoplay, autoplaySpeed, goToSlide } =
    useResultsContext();
  const isActive = activeIndex === index;

  const RADIUS_DESKTOP = 26;
  const SIZE_DESKTOP = 56;
  const circumference = 2 * Math.PI * RADIUS_DESKTOP;

  const svgStyle = {
    "--autoplay-speed": autoplaySpeed,
    "--stroke-dasharray": circumference,
    "--stroke-dashoffset-initial": circumference,
  } as React.CSSProperties;

  const handleClick = () => {
    goToSlide(index);
    setActiveIndex(index);
    if (autoplay) {
      setAutoplay(false);
    }
  };

  return (
    <button
      className={cn(
        styles.thumbnailWrapper,
        "relative rounded-full w-[56px] h-[56px] p-1 cursor-pointer",
        {
          [styles.active]: autoplay && isActive,
        },
        className
      )}
      onClick={handleClick}
      aria-label={`View testimonial for ${name}`}
    >
      <Picture
        src={thumbnail}
        className={cn(styles.thumbnailImage, "w-12 h-12 rounded-full opacity-40", {
          [styles.active]: isActive,
        })}
        alt={`Select to view testimonial for ${name}`}
      />
      {autoplay && isActive ? (
        <svg
          className={cn(styles.progress, styles.desktopProgress, "absolute inset-0")}
          width={SIZE_DESKTOP}
          height={SIZE_DESKTOP}
          style={svgStyle}
        >
          <circle
            cx={SIZE_DESKTOP / 2}
            cy={SIZE_DESKTOP / 2}
            r={RADIUS_DESKTOP}
            fill="transparent"
          />
        </svg>
      ) : null}
    </button>
  );
}

ResultsThumbnail.displayName = "Results.Thumbnail";
